import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, type Eckdaten as EckdatenDaten } from '../api/client';
import { eckdatenAendern } from '../aktionen';
import { useUndo } from '../undo';
import { Schritte } from '../components/Schritte';
import { Feld, Karte, Meldung, fehlerText } from '../components/ui';
import { ART_TEXT, ausLokal, zuLokal } from '../format';
import { gehe, link } from '../router';

/** Schritt 1: Eckdaten der Veranstaltung (neu oder bearbeiten). */
export function Eckdaten({ id, darf }: { id: string | null; darf: boolean }) {
  const [titel, setTitel] = useState('');
  const [art, setArt] = useState('laternenumzug');
  const [start, setStart] = useState('');
  const [teilnehmer, setTeilnehmer] = useState(100);
  const [besonderheiten, setBesonderheiten] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const undo = useUndo();
  const stand = undo.stand;
  const alt = useRef<EckdatenDaten | null>(null);
  const [gespeichert, setGespeichert] = useState(false);

  useEffect(() => {
    if (!id) return;
    api
      .veranstaltung(id)
      .then(({ veranstaltung: v }) => {
        setTitel(v.titel);
        setArt(v.art);
        setStart(zuLokal(v.startzeit));
        setTeilnehmer(v.teilnehmer);
        setBesonderheiten(v.besonderheiten.join(', '));
        alt.current = {
          titel: v.titel,
          art: v.art,
          startzeit: v.startzeit,
          teilnehmer: v.teilnehmer,
          besonderheiten: v.besonderheiten,
        };
      })
      .catch((e: unknown) => setFehler(fehlerText(e)));
  }, [id, stand]);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    const daten = {
      titel,
      art,
      startzeit: ausLokal(start) ?? '',
      teilnehmer,
      besonderheiten: besonderheiten
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    };
    try {
      if (id) {
        if (alt.current) await undo.tue(eckdatenAendern(id, alt.current, daten));
        else await api.veranstaltungAendern(id, daten);
        alt.current = daten;
        setGespeichert(true);
      } else {
        const r = await api.veranstaltungAnlegen(daten);
        gehe(link('v', r.veranstaltung.id, 'vorgaben'));
      }
    } catch (err) {
      setFehler(fehlerText(err));
    }
  }

  return (
    <div className="seite">
      {id && <Schritte id={id} aktiv="eckdaten" titel={titel} />}
      <form className="seite__schmal" onSubmit={(e) => void speichern(e)}>
        <Karte titel={id ? 'Eckdaten' : 'Neue Veranstaltung'}>
          <fieldset disabled={!darf} className="formular">
            <Feld label="Titel">
              <input
                value={titel}
                onChange={(e) => setTitel(e.target.value)}
                required
                maxLength={200}
              />
            </Feld>
            <Feld label="Art">
              <select value={art} onChange={(e) => setArt(e.target.value)}>
                {Object.entries(ART_TEXT).map(([k, t]) => (
                  <option key={k} value={k}>
                    {t}
                  </option>
                ))}
              </select>
            </Feld>
            <Feld label="Start (Aufstellung Zugspitze)">
              <input
                type="datetime-local"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                required
              />
            </Feld>
            <Feld label="Teilnehmerzahl" hinweis="Bestimmt mit dem Regelwerk die Zuglänge.">
              <input
                type="number"
                min={1}
                max={100000}
                value={teilnehmer}
                onChange={(e) => setTeilnehmer(Number(e.target.value))}
                required
              />
            </Feld>
            <Feld
              label="Besonderheiten im Zug"
              hinweis="Durch Komma getrennt, z. B. Spielmannszug, Pferd, Fackeln"
            >
              <input value={besonderheiten} onChange={(e) => setBesonderheiten(e.target.value)} />
            </Feld>
          </fieldset>
          {fehler && <Meldung art="fehler">{fehler}</Meldung>}
          {gespeichert && <Meldung art="erfolg">Gespeichert.</Meldung>}
          {darf && (
            <div className="knopfreihe">
              <button type="submit" className="knopf knopf--primaer">
                {id ? 'Speichern' : 'Anlegen und weiter zu den Vorgaben'}
              </button>
              {id && (
                <a className="knopf" href={link('v', id, 'vorgaben')}>
                  Weiter: Vorgaben übernehmen
                </a>
              )}
            </div>
          )}
        </Karte>
      </form>
    </div>
  );
}
